import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';

import { Modal } from 'UIPackage/ModalModule/components/Modal';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { Button } from 'UIPackage/MainModule/components/Button';
import { Checkbox } from 'UIPackage/MainModule/components/Checkbox';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { ModalHeader } from 'UIPackage/ModalModule/components/ModalHeader';
import { ModalBody } from 'UIPackage/ModalModule/components/ModalBody';
import { ModalActions } from 'UIPackage/ModalModule/components/ModalActions';

import { IErknmAnalyticsTableColumn } from '../interfaces/IErknmAnalyticsTableColumn';
import styles from './ErknmAnalyticsColumnModal.module.scss';
import { erknmAnalyticsGetAdditionalColumns, erknmAnalyticsSetTableColumns } from '../actions/erknmAnalyticsActions';
import { getErknmAnalyticsTableColumns } from '../selectors/erknmAnalyticsSelectors';
import { convertColumnsDataToTableColumns } from '../common/convertColumnsDataToTableColumns';

interface IErknmAnalyticsColumnModalProps {
  modifiedColumn: IErknmAnalyticsTableColumn | null;
  tableColumns: IErknmAnalyticsTableColumn[];
  onClose: () => void;
}

const sortSelectedColumns = (a: IErknmAnalyticsTableColumn, b: IErknmAnalyticsTableColumn) =>
  a.id.localeCompare(b.id, undefined, { numeric: true, sensitivity: 'base' });

const ErknmAnalyticsColumnModalWithoutStore = ({
  modifiedColumn,
  tableColumns,
  onClose,
}: IErknmAnalyticsColumnModalProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const columns: IErknmAnalyticsTableColumn[] | undefined = convertColumnsDataToTableColumns(
    tableColumns[tableColumns.findIndex(item => item.id === modifiedColumn?.id) + 1].cs,
  );
  const [selectedColumns, setSelectedColumns] = useState<IErknmAnalyticsTableColumn[]>([]);

  useEffect(() => {
    if (modifiedColumn?.id) {
      dispatch(erknmAnalyticsGetAdditionalColumns(modifiedColumn.id));
    }
  }, [dispatch, modifiedColumn?.id]);

  useEffect(() => {
    if (modifiedColumn?.id) {
      setSelectedColumns(tableColumns.filter(item => item.mainId === modifiedColumn?.id));
    }
  }, [tableColumns, modifiedColumn?.id]);

  const onCheckboxChange = (column: IErknmAnalyticsTableColumn) => (e: ChangeEvent<HTMLInputElement>) => {
    if (modifiedColumn?.id) {
      if (e.target.checked) {
        setSelectedColumns(prev => [...prev, { ...column, mainId: modifiedColumn.id, isActive: true }]);
      } else {
        setSelectedColumns(prev => [...prev.filter(item => item.id !== column.id)]);
      }
    }
  };

  const onShow = useCallback(() => {
    if (modifiedColumn?.id) {
      dispatch(
        erknmAnalyticsSetTableColumns(
          [
            ...tableColumns.filter(item => item.mainId !== modifiedColumn?.id && item.id !== 'name'),
            ...selectedColumns.map(col => ({ ...col, isActive: true })),
          ].sort(sortSelectedColumns),
        ),
      );
    }
    onClose();
  }, [dispatch, modifiedColumn?.id, onClose, selectedColumns, tableColumns]);

  return (
    <Modal visible={!!modifiedColumn} onClose={onClose} modalBodyClassName={styles.Modal}>
      <ModalHeader title="Показать данные" />
      <ModalBody>
        <div className={styles.ColumnName}>{modifiedColumn?.name}</div>
        {columns && columns.length > 0 ? (
          <ul className={styles.ColumnList}>
            {columns.map((item, index) => (
              <li className={styles.ColumnItem} key={index}>
                <Checkbox
                  className={styles.Checkbox}
                  id={`${item.id}`}
                  theme="system"
                  onChange={onCheckboxChange(item)}
                  checked={!!selectedColumns.find(selectedColumn => selectedColumn.id === item.id)}
                />
                <label htmlFor={`${item.id}`}>Из них: {item.name}</label>
              </li>
            ))}
          </ul>
        ) : (
          <div>Нет данных</div>
        )}
      </ModalBody>
      <ModalActions>
        <Button type="button" color="primary" size="medium" text="Показать данные" onClick={onShow} />
        <Button color="default" text="Отменить" size="medium" type="button" onClick={onClose} />
      </ModalActions>
    </Modal>
  );
};

const mapStateToProps = (state: IRootState) => ({
  tableColumns: getErknmAnalyticsTableColumns(state),
});

export const ErknmAnalyticsColumnModal = connect(mapStateToProps)(ErknmAnalyticsColumnModalWithoutStore);
